import { lazy, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAlertStore from '../../stores/alertStore';
import LoadingAnim from '../../assets/anims/loading-anim.json';
import Lottie from 'lottie-react';
import { fetchTenantRegistration, finishTenantRegistration } from '../../services/tenantRegistrationService';
import Topbar from './components/topbar/topbar';
import { ILease } from '../../services/leaseService';
import useCustomNavigate from '../../hooks/useCustomNavigate';
import { useTranslation } from 'react-i18next';

// Lazy load components
const ReviewContractDetails = lazy(() => import('./sub-pages/review-contract-details/review-contract-details'));
const PaymentMethodSetup = lazy(() => import('./sub-pages/payment-method-setup/payment-method-setup'));
const ReviewDocuments = lazy(() => import('./sub-pages/review-documents/review-documents'));
const Finished = lazy(() => import('./sub-pages/finished/finished'));

const TenantRegistrationPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useCustomNavigate();
    const { i18n } = useTranslation();

    // Stores
    const showAlert = useAlertStore(state => state.showAlert);

    // Stripe is only available if the publishable key is configured
    const isStripeEnabled = Boolean(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

    // States
    const [tenancyContract, setTenancyContract] = useState<ILease | null>(null);
    const [isInitialLoaded, setIsInitialLoaded] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const token = searchParams.get('token');
        i18n.changeLanguage('en');

        if (!token) {
            showAlert("error", "No authentication token provided in URL");
            navigate("/home");
            return;
        }

        const loadContractDetails = async () => {
            try {
                setIsLoading(true);
                const tenancyContract = await fetchTenantRegistration(token);
                setTenancyContract(tenancyContract);

                // Determine which step to resume at
                // Step 0: Contract Details (opt_in_date = null means not completed)
                // Step 1: Payment Method (only if stripe_eligible, otherwise skip)
                // Step 2: Upload Documents
                if (tenancyContract.preferred_stripe_payment) {
                    // Already chose Stripe → skip to Upload Documents (step 2)
                    setCurrentStep(2);
                } else if (tenancyContract.opt_in_date) {
                    if (isStripeEnabled && tenancyContract.stripe_eligible) {
                        // Contract details completed, eligible for Stripe → show Payment Method (step 1)
                        setCurrentStep(1);
                    } else {
                        // Not eligible → skip Payment Method, go to Upload Documents (step 2)
                        setCurrentStep(2);
                    }
                } else {
                    setCurrentStep(0);
                }

                setIsInitialLoaded(true);
            } catch (error) {
                console.log(`Error while fetching tenancy contract:`, error);
                // @ts-ignore
                showAlert("error", error.message);
            } finally {
                setIsLoading(false);
            }
        }
        loadContractDetails();
    }, [searchParams]);

    const onClickFinish = async () => {
        const token = searchParams.get('token');

        if (!token) {
            showAlert("error", "No authentication token provided in URL");
            return;
        }

        try {
            setIsLoading(true);

            await finishTenantRegistration(token);
            setCurrentStep(3);
        } catch (error) {
            console.log(`Error while finishing tenant registration:`, error);
            // @ts-ignore
            showAlert("error", error.message);
        } finally {
            setIsLoading(false);
        }
    }

    if (!tenancyContract) return null;

    return (
        <div>
            <Topbar currentStep={currentStep} stripeEligible={isStripeEnabled && tenancyContract.stripe_eligible} />

            {isInitialLoaded && !isLoading ? (
                <>
                    {currentStep === 0 && (
                        <ReviewContractDetails
                            token={searchParams.get('token') || ""}
                            tenancyContract={tenancyContract}
                            onClickNext={(updatedLease) => {
                                setTenancyContract(updatedLease);
                                // Skip payment method step if not eligible for Stripe
                                setCurrentStep(isStripeEnabled && updatedLease.stripe_eligible ? 1 : 2);
                            }}
                        />
                    )}
                    {currentStep === 1 && (
                        <PaymentMethodSetup
                            token={searchParams.get('token') || ""}
                            tenancyContract={tenancyContract}
                            onClickNext={(updatedLease?: ILease) => {
                                if (updatedLease) {
                                    setTenancyContract(updatedLease);
                                }
                                setCurrentStep(2);
                            }}
                        />
                    )}
                    {currentStep === 2 && (
                        <ReviewDocuments
                            isLoading={isLoading}
                            token={searchParams.get('token') || ""}
                            tenancyContract={tenancyContract}
                            onClickNext={onClickFinish}
                        />
                    )}
                    {currentStep === 3 && (
                        <Finished />
                    )}
                </>
            ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', marginTop: 100 }}>
                    <Lottie animationData={LoadingAnim} loop={true} style={{ height: 100 }} />
                </div>
            )}
        </div>
    );
};

export default TenantRegistrationPage;
